import { GRAPH_FIELD_TYPE } from './dataKiGraph';
import {
  DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS,
  type DataModelTransitionGraphFieldItem,
} from './dataModelTransitionGraph';

/**
 * グラフ API を何回・どの項目で叩くかを決める（取得用）。
 * fields があればそれを使う。なければ field。どちらもなければアウト（out）。
 */
export const resolveDataModelTransitionGraphFields = (params: {
  field?: string;
  fields?: string[];
}): string[] => {
  if (params.fields && params.fields.length > 0) {
    return params.fields.slice(0, DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS);
  }
  if (params.field) {
    return [params.field];
  }
  return [GRAPH_FIELD_TYPE.OUT];
};

/**
 * 「推移グラフを表示」押下時に API へ送る項目一覧。
 * 既にグラフ表示済みなら graphList 由来の項目を維持し、初回のみアウト1本。
 */
export const resolveDataModelTransitionGraphFieldsForSearch = (
  existingGraphParams: { field?: string; fields?: string[] } | undefined
): string[] => {
  if (existingGraphParams) {
    return resolveDataModelTransitionGraphFields(existingGraphParams);
  }
  return [GRAPH_FIELD_TYPE.OUT];
};

/**
 * お気に入り適用時に表示するグラフ項目一覧を決める。
 * setting.fields → setting.field（複数項目表示対応前）→ デフォルト out の順で resolve する。
 */
export const resolveDataModelTransitionGraphFieldsFromFavorite = (setting: {
  field?: string;
  fields?: string[];
}): string[] => {
  return resolveDataModelTransitionGraphFields(setting);
};

/**
 * グラフ追加後の項目コード配列を組み立てる。
 */
export const buildGraphFieldsAfterAdd = (
  currentFields: readonly string[],
  newField: string,
  maxFields: number = DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => {
  if (currentFields.length >= maxFields) {
    return currentFields.slice(0, maxFields);
  }
  return [...currentFields, newField].slice(0, maxFields);
};

/**
 * 未選択スロット（空文字 ''）を末尾に追加する。
 * 「グラフを追加」時に使う。軸枠のみ表示し、項目選択後に API 取得する。
 */
export const buildGraphFieldsAfterAddEmptySlot = (
  currentFields: readonly string[],
  maxFields: number = DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => buildGraphFieldsAfterAdd(currentFields, '', maxFields);

/**
 * グラフ項目の変更で API 再取得が必要かどうか。
 *
 * fieldSlots には未選択枠用の空文字 '' が含まれる。比較前に '' を除いた
 * 「実際に取得する項目」だけを見る。例: ['out'] → ['out', ''] は再取得不要（false）。
 */
export const shouldRefetchDataModelTransitionGraphFields = (
  prevFields: readonly string[],
  nextFields: readonly string[]
): boolean => {
  const normalizedPrev = normalizeGraphFieldsForFetch([...prevFields]);
  const normalizedNext = normalizeGraphFieldsForFetch([...nextFields]);
  return normalizedPrev.join(',') !== normalizedNext.join(',');
};

/**
 * 変更後に残っている項目が、変更前と同じ並び順かどうかを判定する。
 *
 * 「変更前からいくつか項目を外しただけ」なら true。項目の差し替えや追加は false。
 *
 * @example
 * // 2本目を未選択にした（稼働だけ外れた）
 * areRemainingGraphFieldsInOrder(['out', 'uriage'], ['out', 'kadoJikan', 'uriage']) // true
 *
 * @example
 * // 稼働をアウト合計に差し替えた
 * areRemainingGraphFieldsInOrder(['out', 'outTotal', 'uriage'], ['out', 'kadoJikan', 'uriage']) // false
 */
const areRemainingGraphFieldsInOrder = (
  remainingFields: readonly string[],
  originalFields: readonly string[]
): boolean =>
  remainingFields.reduce<{ searchIndex: number; isMatch: boolean }>(
    (state, fieldCode) => {
      if (!state.isMatch) {
        return state;
      }
      // 前回見つけた位置より後ろから、同じ項目コードを探す
      const relativeIndex = originalFields
        .slice(state.searchIndex)
        .findIndex((candidate) => candidate === fieldCode);
      if (relativeIndex === -1) {
        return { searchIndex: state.searchIndex, isMatch: false };
      }
      return {
        searchIndex: state.searchIndex + relativeIndex + 1,
        isMatch: true,
      };
    },
    { searchIndex: 0, isMatch: true }
  ).isMatch;

/**
 * グラフ項目の変更が「取得済みデータの削除だけ」かどうか。
 *
 * true: API を叩かず graphList から該当項目だけ外す（setDataModelTransitionGraphGraphListAction）。
 * false: 項目の追加・差し替えがあるため API 再取得が必要。
 *
 * セレクトで「未選択」にした枠は項目コードが空文字 '' になるが、判定には使わない
 * （未選択＝その項目のグラフを表示しない、という意味なので）。
 *
 * @example
 * // 3本表示中に2本目（稼働）を未選択にした
 * // 画面: ['out', '', 'uriage']
 * // 判定時: ['out', 'uriage'] と ['out', 'kadoJikan', 'uriage'] を比較 → 稼働だけ外れた → true
 */
export const isGraphFieldsRemovalOnly = (
  prevFields: readonly string[],
  nextFields: readonly string[]
): boolean => {
  const normalizedPrev = normalizeGraphFieldsForFetch([...prevFields]);
  const normalizedNext = normalizeGraphFieldsForFetch([...nextFields]);
  if (normalizedNext.length >= normalizedPrev.length) {
    return false;
  }
  return areRemainingGraphFieldsInOrder(normalizedNext, normalizedPrev);
};

/**
 * グラフ削除後の項目コード配列を組み立てる（最低1本は残す）。
 */
export const buildGraphFieldsAfterRemove = (
  currentFields: readonly string[],
  index: number,
  maxFields: number = DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => {
  if (index < 0 || index >= currentFields.length || currentFields.length <= 1) {
    return currentFields.slice(0, maxFields);
  }
  return currentFields
    .filter((_, fieldIndex) => fieldIndex !== index)
    .slice(0, maxFields);
};

/**
 * グラフ追加時に選ぶ項目。未使用のうち availableFields の先頭。すべて使用中なら先頭項目（重複可）。
 */
export const pickDefaultFieldForNewGraph = (
  currentFields: readonly string[],
  availableFields: readonly { code: string }[],
  fallback: string = GRAPH_FIELD_TYPE.OUT
): string => {
  const usedFieldCodes = new Set(currentFields.filter((field) => field !== ''));
  const unusedField = availableFields.find(
    (field) => !usedFieldCodes.has(field.code)
  );
  return unusedField?.code ?? availableFields.at(0)?.code ?? fallback;
};

/**
 * グラフ項目セレクト変更後の、表示項目コード配列を組み立てる。
 * @param currentFields いま表示している項目コード一覧
 * @param index 変更対象グラフの並び順（0始まり）
 * @param field 選んだ項目コード。空文字なら当該スロットを未選択枠（''）にする
 */
export const buildGraphFieldsAfterIndexChange = (
  currentFields: readonly string[],
  index: number,
  field: string,
  maxFields: number = DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => {
  if (index < 0 || index >= maxFields) {
    return currentFields.slice(0, maxFields);
  }

  if (field === '') {
    const slotCount = Math.min(
      Math.max(currentFields.length, index + 1),
      maxFields
    );
    return Array.from({ length: slotCount }, (_, slotIndex) =>
      slotIndex === index ? '' : currentFields.at(slotIndex) ?? ''
    );
  }

  const slotCount = Math.min(
    Math.max(currentFields.length, index + 1),
    maxFields
  );

  return Array.from({ length: slotCount }, (_, slotIndex) =>
    slotIndex === index
      ? field
      : currentFields.at(slotIndex) ?? GRAPH_FIELD_TYPE.OUT
  );
};

/**
 * API 再取得用に fields を整える。未選択枠（''）を除き、最大本数で切る。
 */
export const normalizeGraphFieldsForFetch = (
  fields: string[],
  maxFields: number = DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => {
  return fields.filter((f) => f !== '').slice(0, maxFields);
};

/**
 * 「枠のN番目 → データの何番目か」を返す。
 *
 * 例:
 *   枠:     [アウト, 未選択, 売上]
 *   データ: [アウト, 売上]
 *   枠の3番目（売上）→ データの2番目（戻り値 1）
 */
export const resolveGraphListIndexForFieldSlot = (
  fieldSlots: readonly string[],
  slotIndex: number
): number => {
  return fieldSlots.slice(0, slotIndex).filter((field) => field !== '').length;
};

/**
 * 画面上のある枠（1本目、2本目…）に描画するグラフデータを返す。
 *
 * - 未選択の枠 → undefined（折れ線は出さず、軸だけ）
 * - 選択済みの枠 → データ一覧から該当するものを返す
 * - 項目差し替え直後など、番号が合わないとき → 項目名（out など）で探す
 */
export const resolveGraphItemForFieldSlot = (
  graphList: readonly DataModelTransitionGraphFieldItem[],
  fieldSlots: readonly string[],
  slotIndex: number
): DataModelTransitionGraphFieldItem | undefined => {
  const field = fieldSlots.at(slotIndex);
  if (field === undefined || field === '') {
    return undefined;
  }
  const graphListIndex = resolveGraphListIndexForFieldSlot(
    fieldSlots,
    slotIndex
  );
  const itemByIndex = graphList.at(graphListIndex);
  if (itemByIndex?.field === field) {
    return itemByIndex;
  }
  return graphList.find((graphItem) => graphItem.field === field);
};

/**
 * グラフ項目を減らしたあと、データ一覧を整理する（APIは叩かない）。
 *
 * 例: アウト・稼働・売上の3本表示で、稼働を未選択にした
 *   → データ一覧から稼働を除き、[アウト, 売上] だけ残す
 */
export const buildGraphListForNormalizedFields = (
  currentGraphList: readonly DataModelTransitionGraphFieldItem[],
  normalizedFields: readonly string[]
): DataModelTransitionGraphFieldItem[] => {
  const graphByField = new Map(
    currentGraphList.map((item) => [item.field, item])
  );
  return normalizedFields.flatMap((field) => {
    const item = graphByField.get(field);
    return item ? [item] : [];
  });
};

/**
 * graphList 更新時に fieldSlots を同期する。
 * graphList が空のときだけクリアし、それ以外は未選択枠（''）を維持する。
 */
export const mergeFieldSlotsFromGraphList = (
  prevSlots: readonly string[],
  graphList: readonly Pick<DataModelTransitionGraphFieldItem, 'field'>[],
  maxFields: number = DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => {
  if (graphList.length === 0) {
    return [];
  }

  const fieldsFromGraph = graphList.map((item) => item.field);
  const normalizedPrev = normalizeGraphFieldsForFetch(
    [...prevSlots],
    maxFields
  );

  if (normalizedPrev.join(',') === fieldsFromGraph.join(',')) {
    return prevSlots.slice(0, maxFields);
  }

  // 部分失敗などで graphList が縮んだ場合でも、ユーザーが選択したスロットは維持する
  if (
    normalizedPrev.length > fieldsFromGraph.length &&
    fieldsFromGraph.every((field, index) => normalizedPrev.at(index) === field)
  ) {
    return prevSlots.slice(0, maxFields);
  }

  const trailingEmptySlots: string[] = [];
  for (let index = fieldsFromGraph.length; index < prevSlots.length; index++) {
    if (prevSlots.at(index) === '') {
      trailingEmptySlots.push('');
    } else {
      break;
    }
  }

  return [...fieldsFromGraph, ...trailingEmptySlots].slice(0, maxFields);
};

export { syncGraphSlotKeys } from './graphSlotKeys';
